// This form is used in all login help options other than "forgot login Id"

import * as React from "react";
import InputField from "../../generic/inputField";
import { emailRegex, pan, alphaNumExp } from "../../utils/regex";
import EdButton from "../../generic/edButton";
import RadioButton from "../../generic/radioButton";
import "../../../assets/scss/components/login/forms/forgotLoginIdForm.scss";
import "../../../assets/scss/components/login/generic/loginInput.scss";
import { IListObj } from "../../types/genericTypes";
import LoginErrMsg from "../generic/loginErrMsg";
import { useRouteMatch } from "react-router-dom";
import { AForgotPassword, AUnblockAccount, AForgotYob } from "../../service/login/loginApiService";

export interface IForgotFormProps {
	onSuccess: (msg: string) => void; // function to be called on the success of called apis
}

// form fields
export interface IForgotFields {
	pan: string;
	email: string;
	loginId: string;
	accTyp?: string;
}

const ForgotForm: React.FC<IForgotFormProps> = props => {
	// used to store the entered values in form
	const [values, setValues] = React.useState<IForgotFields>({ pan: "", email: "", loginId: "" });

	// used to store the field wise errors
	const [errors, setErrors] = React.useState<{ [key in keyof IForgotFields]?: string }>({
		pan: undefined,
		email: undefined,
		loginId: undefined
	});

	// used to store the error message that comes from api
	const [errorMsg, setErrorMsg] = React.useState("");

	// used to store if the form is submitted or not
	const [submitted, setSubmitted] = React.useState(false);

	// to check the route
	const routeParams = useRouteMatch();

	const accTyp: Array<IListObj> = [
		{ value: "EQ", display: "Equity" },
		{ value: "CO", display: "Commodity" }
	];

	function handleSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		setSubmitted(true);

		// call different api based on path
		switch (routeParams.path.split("/")[2]) {
			case "forgot-password":
				AForgotPassword(apiRes, values);
				break;
			case "unblock-account":
				AUnblockAccount(apiRes, values);
				break;
			case "forgot-yob":
				AForgotYob(apiRes, values);
		}
	}

	function getButtonValue() {
		// call different api based on path
		switch (routeParams.path.split("/")[2]) {
			case "forgot-password":
				return "Send new password to my email";
			case "unblock-account":
				return "Unblock Account";
			case "forgot-yob":
				return "Send YOB to my email";
			default:
				return "";
		}
	}

	function apiRes(res: any) {
		if (res.data) {
			// if success, then call the onSuccess function in prop
			props.onSuccess(res.data.msg);
		} else if (res.error) {
			// else enable form again
			setSubmitted(false);

			if (res.error.errCd === "ELGH0001") {
				// show account type radio
				setValues({ ...values, accTyp: "EQ" });
			} else {
				// hide account type radio and error msg block
				const { pan, email, loginId } = values;
				setValues({ pan, email, loginId });
				setErrorMsg(res.error.errMsg);
			}
		}
	}

	function onChange(key: keyof IForgotFields, value: string) {
		if ((key === "loginId" || key === "pan") && !alphaNumExp.test(value)) {
			value = values[key];
		}

		// onchange of any field remove both type of errors
		setValues({ ...values, [key]: value });
		setErrors({ ...errors, [key]: "" });
		setErrorMsg("");

		// return value to be cahnged inside the input field
		return value;
	}

	return (
		<form className="login_form">
			<label className="inp_lbl mg-b5 ib">Login ID</label>
			<InputField
				onChange={loginId => onChange("loginId", loginId)}
				onBlur={error => {
					setErrors({ ...errors, loginId: error.required ? "Field is required" : "" });
				}}
				maxLength={24}
				className="login_inp mg-b40"
				placeholder="Eg:5678987"
				required
				errorText={errors.loginId}
			/>

			<label className="inp_lbl mg-b5 ib">Email ID</label>
			<InputField
				pattern={emailRegex}
				required
				onChange={email => onChange("email", email)}
				onBlur={error => setErrors({ ...errors, email: error.pattern ? "" : "Please enter a valid email address" })}
				className="login_inp mg-b40"
				placeholder="Eg: sarveshpali@gmail.com"
				errorText={errors.email}
			/>

			<label className="inp_lbl mg-b5 ib">PAN No.</label>
			<InputField
				onChange={pan => onChange("pan", pan)}
				onBlur={error => setErrors({ ...errors, pan: error.pattern ? "" : "Please enter a valid PAN" })}
				className="login_inp mg-b40"
				placeholder="Eg: JUKL5678987"
				helpTextColor="blue"
				required
				pattern={pan}
				errorText={errors.pan}
				maxLength={10}
			/>

			{/* This select account type block will only show if "ELGH0001" error comes in response to api */}
			{values.accTyp && (
				<div className="same_acc">
					<p className="inp_lbl mg-b15">Select Account type to retrieve Login ID:</p>
					<ul className="same_acc_radio mg-b45">
						<li className="list_Item">
							{accTyp.map(typ => (
								<RadioButton
									key={typ.value}
									list={typ}
									name={"sameAcc"}
									selected={values.accTyp}
									onChange={() => onChange("accTyp", typ.value as string)}
								/>
							))}
						</li>
					</ul>
				</div>
			)}

			{/* Show error if any */}
			{errorMsg && <LoginErrMsg className="mg-b40">{errorMsg}</LoginErrMsg>}

			<EdButton
				value={getButtonValue()}
				btnTyp="primary"
				className="submit_btn"
				// Disable if there is even one error or if any field is empty or if form is submitted
				disabled={Object.values(errors).every(error => error) || Object.values(values).some(value => !value) || submitted}
				onClick={handleSubmit}
			/>
		</form>
	);
};

export default ForgotForm;
